/**
 * `P1-SIM-013`: the instalment formulas, mirrored for OPTIMISTIC DISPLAY.
 *
 * **PROVENANCE, and it travels with the code the way the zone centroids' does.**
 * Every function here is a second spelling of one that lives in
 * `services/api/src/modules/simulation/domain`. The server's copy is the
 * authority , it is what a client is quoted, what a dossier stores and what a
 * bank signs. This copy exists for one purpose: a slider that moves under a
 * finger without a round trip to Bamako on a phone network.
 *
 * **Round 68 2b, and it is why this file has a spec on the other side of the
 * repository.** A display copy exists only as one-source-generated or
 * EQUIVALENCE-PINNED, with zero unpinned copies. This one is equivalence
 * pinned: `services/api/test/unit/sim-mirror.spec.ts` runs both sides against
 * canon's own fixtures , Traoré, Samaké, the duration and deposit boundaries ,
 * and then against **10 000 random inputs**, asserting identical output every
 * time. If the two ever disagree by one franc, that spec is red before anybody
 * sees a screen.
 *
 * **Generated was considered and refused, and the reason matters.** The server
 * formula is thirty lines of arithmetic with three branches; a generator that
 * emitted it would be a second tool to maintain, and its output would still
 * need the property test to prove it correct. The test IS the mechanism: it
 * costs one file, it fails on the first divergent franc, and it needs no build
 * step that could itself go stale. Two copies with a test between them is one
 * fact stated twice on purpose , the same argument `mirror.spec.ts` makes for
 * the label list, and the same one `MAY_INVITE_ROLES` makes for the roles.
 *
 * **THE CONSTANTS ARE NOT MIRRORED, and that is the sharpest line in this
 * file.** No rate, no ceiling, no reference duration appears here. Every
 * function takes its settings as an ARGUMENT, and a screen gets them from
 * `GET /api/v1/simulation/config`. `P1-SIM-002` exists because a constant
 * copied into a screen does not FAIL when it goes stale , it keeps answering,
 * confidently, with last quarter's rate , and a mirror that carried `10.9`
 * would be that failure with a test in front of it proving the arithmetic was
 * right about the wrong number.
 *
 * **What a caller must know.** This answers an ESTIMATE for a slider. The
 * number a client is held to is the server's, and WF-CLI-04's own copy says
 * `Estimation indicative. Le taux définitif est fixé par la banque.`
 *
 * **Pure by construction.** No framework, no IO, no clock, no imports.
 */

/** The settings a screen reads from `GET /api/v1/simulation/config`. Never hard-coded here. */
export interface MirroredSettings {
  readonly borrowerInsuranceRatePct: number;
  readonly effortCeilingPct: number;
  readonly effortComfortPct: number;
}

export interface MirroredInstalment {
  /** The amortised capital and interest, unrounded. */
  readonly capitalPart: number;
  /** The borrower insurance premium, unrounded. */
  readonly insurancePart: number;
  /** What a client is shown: the two, added, rounded ONCE. FCFA. */
  readonly amount: number;
}

/**
 * 02, 2.6.1's instalment.
 *
 * **Rounded once, on the sum.** Rounding the two parts and adding them drifts
 * by a franc on some inputs, and a franc between the slider and the quote is
 * the divergence a client would be right to distrust , which is exactly what
 * this mirror exists not to introduce.
 *
 * **A zero rate is a real case**, not a division by zero: `1 - (1+0)^-n` is
 * zero and the closed form is undefined, so a straight line is the answer. A
 * subsidised line is not hypothetical, and the day one exists this must not
 * show `Infinity`.
 */
export function mensualiteMirror(
  amountFinanced: number,
  durationMonths: number,
  nominalRatePct: number,
  settings: MirroredSettings,
): MirroredInstalment | null {
  // `null` rather than a throw: a slider is moved, not submitted, and a screen
  // that threw on an intermediate position would need a try/catch around a
  // drag gesture. The server refuses by NAME; this refuses by absence.
  if (!Number.isFinite(amountFinanced) || amountFinanced <= 0) return null;
  if (!Number.isInteger(durationMonths) || durationMonths <= 0) return null;
  if (!Number.isFinite(nominalRatePct) || nominalRatePct < 0) return null;

  const monthlyRate = nominalRatePct / 100 / 12;
  const capitalPart =
    monthlyRate === 0
      ? amountFinanced / durationMonths
      : (amountFinanced * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -durationMonths));
  const insurancePart = (amountFinanced * (settings.borrowerInsuranceRatePct / 100)) / 12;

  return { capitalPart, insurancePart, amount: Math.round(capitalPart + insurancePart) };
}

/**
 * 02, 2.6.5's financed amount. **Floored**, because a financed amount is a
 * whole number of francs and rounding up would finance a franc nobody asked to
 * borrow.
 */
export function montantFinanceMirror(price: number, depositPct: number): number | null {
  if (!Number.isFinite(price) || price <= 0) return null;
  // `>= 100` and not `> 100`: a hundred per cent deposit is the absence of
  // financing (`DECISION ADDED 05-38`), and the mirror must refuse exactly
  // where the server refuses or a slider offers a position the quote rejects.
  if (depositPct < 0 || depositPct >= 100) return null;
  return Math.floor(price * (1 - depositPct / 100));
}

/**
 * `SYNTHESIS-FIELDS`, M2: the apport in francs, **the price less the amount financed by `montantFinanceMirror`**, which is the split the buyer's own quote prints
 * (`depositAmount: price - amountFinanced`, `simulation-api.service.ts`, the financed amount being `montantFinance`'s, equal to the mirror by `mirror.spec.ts`). The
 * franc a floored financed amount leaves is the apport's, as on the buyer's screen. Not a second formula: the one floor, one subtraction. `null` where the mirror is.
 */
export function depositAmountMirror(price: number, depositPct: number): number | null {
  const financed = montantFinanceMirror(price, depositPct);
  return financed === null ? null : price - financed;
}

/** 02, 2.6.3's effort, as a whole percentage. */
export function effortMirror(
  instalmentAmount: number,
  existingLoanInstalmentAmount: number,
  netSalaryAmount: number,
): number | null {
  if (!Number.isFinite(netSalaryAmount) || netSalaryAmount <= 0) return null;
  if (!Number.isFinite(instalmentAmount) || instalmentAmount < 0) return null;
  if (!Number.isFinite(existingLoanInstalmentAmount) || existingLoanInstalmentAmount < 0) return null;
  return Math.round(((instalmentAmount + existingLoanInstalmentAmount) / netSalaryAmount) * 100);
}

/** 02, 2.6.3's three bands. Both thresholds INCLUSIVE, and both come from the settings. */
export type MirroredBand = "confortable" | "acceptable" | "au-dela";

export function effortBandMirror(
  effortPct: number,
  settings: Pick<MirroredSettings, "effortComfortPct" | "effortCeilingPct">,
): MirroredBand {
  if (effortPct <= settings.effortComfortPct) return "confortable";
  if (effortPct <= settings.effortCeilingPct) return "acceptable";
  return "au-dela";
}

/**
 * 02, 2.6.3's Hakili capacity: `round(salaire * ceiling%) - pret`.
 *
 * The existing loan is subtracted AFTER the ceiling is applied , 2.6.3's own
 * order. The ceiling is a share of INCOME, so a client already paying 45 000
 * has that much less of their share left, not a smaller share.
 *
 * **`quotiteCessible` is deliberately NOT mirrored.** It is the BANK's 40 %
 * rule (`DECISION ADDED 4`), no screen shows it, and a mirror carrying it would
 * be the merged-ratio failure that decision exists to prevent, on the client's
 * own device.
 */
export function capaciteMirror(
  netSalaryAmount: number,
  existingLoanInstalmentAmount: number,
  settings: MirroredSettings,
): number | null {
  if (!Number.isFinite(netSalaryAmount) || netSalaryAmount <= 0) return null;
  if (!Number.isFinite(existingLoanInstalmentAmount) || existingLoanInstalmentAmount < 0) return null;
  return Math.round((netSalaryAmount * settings.effortCeilingPct) / 100) - existingLoanInstalmentAmount;
}

/**
 * Round `L-1`, `M3`: 02 §2.6.2's TAEG, mirrored for the bank's composer so
 * the recap moves live while the analyst types, and the door remains the
 * truth at the save. `nominal + 0,9 point of borrower insurance + the fees'
 * annualised impact`, the fees amortised over 24 months , the same
 * expression `simulation/domain/taeg.ts` computes, compared to it by
 * `mirror.spec.ts` over ten thousand draws. Only the BUYER's fees enter; they
 * enter whether financed or paid at the signature. `null` where a figure is
 * not one.
 */
export const TAEG_INSURANCE_POINTS = 0.9;
export const TAEG_FEE_AMORTISATION_MONTHS = 24;

export function taegMirror(
  nominalRatePct: number,
  feesTotalAmount: number,
  baseAmount: number,
  durationMonths: number,
): number | null {
  if (!Number.isFinite(nominalRatePct) || nominalRatePct <= 0) return null;
  if (!Number.isFinite(feesTotalAmount) || feesTotalAmount < 0) return null;
  if (!Number.isFinite(baseAmount) || baseAmount <= 0) return null;
  if (!Number.isInteger(durationMonths) || durationMonths <= 0) return null;
  const feeImpact = ((feesTotalAmount / baseAmount) * 100 * TAEG_FEE_AMORTISATION_MONTHS) / durationMonths;
  return Math.round((nominalRatePct + TAEG_INSURANCE_POINTS + feeImpact) * 10) / 10;
}

/**
 * Round `L-1`, `M3`: the composer's three roundings, kept HERE , the one file
 * admitted to round (the rule `hakili/no-money-float`'s fourth home) , so
 * `composer.ts` rounds nothing of its own. Each is one expression.
 */
/** A one-decimal rate as the wire carries it (« 14.0 », never « 14 »). */
export function oneDecimalLabel(rate: number): string {
  const tenths = Math.round(rate * 10);
  const sign = tenths < 0 ? "-" : "";
  const whole = Math.trunc(Math.abs(tenths) / 10);
  return `${sign}${String(whole)}.${String(Math.abs(tenths) % 10)}`;
}
/**
 * `GRID-TOLERANCE-EDGE`: whether an offered rate is BELOW the grid's rate minus the policy's tolerance, compared in whole tenths (D2 `core.js:102`: `r1(rate) < r1(grid - tolerance)`),
 * so a rate exactly AT the edge is not below it (`10.4 - 0.2` is `10.200000000000001` in floats; in tenths it is 102). The one comparison every site reads.
 */
export function rateBelowGridEdge(rate: number, gridRate: number, tolerance: number): boolean {
  return tenthsOf(rate) < tenthsOf(gridRate) - tenthsOf(tolerance);
}
/** `COMPOSER-GAPS`: the signed distance of a rate from a reference rate in points, one decimal, rounded here and once (`0`, never `-0`). */
export function ratePointsFrom(rate: number, reference: number): number {
  const tenths = Math.round((rate - reference) * 10 + 1e-9 * Math.sign(rate - reference));
  return tenths === 0 ? 0 : tenths / 10;
}
/** `OD-37` round 8: a grade score or mean to one decimal, half up, rounded once (the grille's rounding convention). */
export function roundTenth(value: number): number {
  return Math.round(value * 10 + 1e-9) / 10;
}
/** The same value in whole tenths , an integer, so a sum of scores never rides a binary float into a threshold. */
export function tenthsOf(value: number): number {
  return Math.round(value * 10 + 1e-9);
}
/** A share in whole percent, `numerator / denominator × 100`, rounded once. */
export function wholePercent(numerator: number, denominator: number): number {
  return Math.round((numerator / denominator) * 100);
}
/** A share in tenths of a percent (« 61,3 % » of the capacity), rounded once. */
export function tenthPercent(numerator: number, denominator: number): number {
  return Math.round((numerator / denominator) * 1000) / 10;
}
/** The insurance's part of the instalment, to the franc, from the mirror's unrounded part. */
export function insurancePartRounded(instalment: MirroredInstalment): number {
  return Math.round(instalment.insurancePart);
}

/**
 * R-B (16 September): 02 §2.6.2's `impactFrais` as the formula line prints
 * it , the fees' share of the base, amortised over 24 months and spread on the
 * term, two decimals at most, the trailing zeros dropped as the drawing drops
 * them (« 1,6 % amortis sur 60 mois », « 2,67 % amortis sur 36 mois »). The
 * TAEG itself is `taegMirror`'s; this is its middle term, printed.
 */
export function feeImpactPercentLabel(
  feesTotalAmount: number,
  baseAmount: number,
  durationMonths: number,
): string {
  if (
    !Number.isFinite(baseAmount) ||
    baseAmount <= 0 ||
    !Number.isInteger(durationMonths) ||
    durationMonths <= 0
  ) {
    return "-";
  }
  const share = ((feesTotalAmount / baseAmount) * 100 * TAEG_FEE_AMORTISATION_MONTHS) / durationMonths;
  const hundredths = Math.round(share * 100);
  const whole = Math.trunc(hundredths / 100);
  const rest = hundredths % 100;
  const decimals = rest === 0 ? "" : rest % 10 === 0 ? String(rest / 10) : String(rest).padStart(2, "0");
  return decimals === "" ? String(whole) : `${String(whole)},${decimals}`;
}

/**
 * Round `L-1b`, `M4` , the drawing's « Produit d'intérêts au taux retenu »
 * (`hakili-bank.jsx`, `DecisionScreen`): the capital instalment over the
 * term, less the capital , the insurance apart, as the drawing computes it.
 * Rounded once, on the sum, here where rounding lives; `null` where the
 * instalment is not defined.
 */
export function interestIncomeMirror(
  amountFinanced: number,
  durationMonths: number,
  nominalRatePct: number,
  settings: Pick<MirroredSettings, "borrowerInsuranceRatePct">,
): number | null {
  const instalment = mensualiteMirror(amountFinanced, durationMonths, nominalRatePct, {
    ...settings,
    effortCeilingPct: 0,
    effortComfortPct: 0,
  });
  if (instalment === null) return null;
  return Math.round(instalment.capitalPart * durationMonths - amountFinanced);
}
